import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FieldFilter, SearchEvent } from '../../../shared/models/search.model';
import { Domain, Language, LocalizedKeyValue, Market, ModerationReason, Status } from '../../../shared/models/dataref.model';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { Observable, of, Subject } from 'rxjs';
import { RefDataService } from '../../../services/ref-data.service';
import { map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { Utils } from '../../../shared/utils/utils';
import { RecipeFilterType, SearchRecipeCommunitiesFormGroup } from '../../../shared/models/recipe/recipe.model';
import { AcpResponseAPI, RecipeSearchField } from '../../../shared/models/recipe/recipe-acp.model';
import { SearchFiltersService } from '@services/search/search-filters.service';

@Component({
  selector: 'app-communities-recipes-search-query',
  templateUrl: './communities-recipes-search-query.component.html',
  standalone: false,
})
export class CommunitiesRecipesSearchQueryComponent implements OnInit, OnChanges, OnDestroy {
  @Input()
    domains: Domain[] | null = [];
  @Input()
    status: Status[] = [];
  @Input()
    markets: Market[] = [];
  @Input()
    languages: Language[] = [];
  @Input()
    recipeReasons: ModerationReason[] = [];
  @Input()
    fieldFilters: FieldFilter[] = [];

  @Output()
    searchChange = new EventEmitter<SearchEvent>();

  filteredDomains: { [group: string]: Domain[] } = {};
  filteredMarkets: Market[] = [];
  filteredLanguages: Language[] = [];

  queryForm: FormGroup<SearchRecipeCommunitiesFormGroup> = this.formBuilder.group({
    domain: new FormControl(),
    market: new FormControl(),
    lang: new FormControl({ value: null as unknown as Language, disabled: true }),
    status: new FormControl(),
    searchTxt: new FormControl(),
    lastModerationReason: new FormControl(),
  });

  destroy$ = new Subject<void>();

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly refDataService: RefDataService,
    private readonly searchFiltersService: SearchFiltersService,
  ) {}

  ngOnInit(): void {
    this.registerDomainChanges();
    this.registerMarketChanges();
    this.registerLangChanges();
    this.registerStatusChanges();
    this.registerRecipeReasonsChanges();
    this.loadMarkets();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.domains?.currentValue) {
      this.filteredDomains = this.searchFiltersService.groupDomainBySSID(changes.domains.currentValue);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  search(): void {
    this.searchChange.emit({
      q: this.queryForm.value.searchTxt!,
      fieldFilters: this.fieldFilters,
      facetFilters: [],
    });
  }

  searchAcp(event: AcpResponseAPI): void {
    this.queryForm.patchValue({ searchTxt: event.query });

    this.updateFieldFilters(event?.fieldFilters?.ingredient, RecipeSearchField.INGREDIENT);
    this.updateFieldFilters(event?.fieldFilters?.recipe, RecipeSearchField.RECIPE);

    this.search();
  }

  getDomainsSSID(): string[] {
    return Object.keys(this.filteredDomains);
  }

  compareByKey(a: LocalizedKeyValue | null, b: LocalizedKeyValue | null): boolean {
    return a?.key === b?.key;
  }

  updateFieldFilters(fieldFilters: { field: string; values: string[] } | undefined, field: string): void {
    if (fieldFilters) {
      if (fieldFilters.values.length > 0) {
        this.fieldFilters.unshift({ field, values: fieldFilters.values });
      } else {
        this.fieldFilters = this.fieldFilters.filter((fieldFilter) => fieldFilter.field !== field);
      }
    }
  }

  /**
   * Modify value for market selector
   * And ensure that market and lang selectors are correctly enabled/disabled
   */
  patchMarketValue(marketValue: Market | null): void {
    this.queryForm.patchValue({
      market: marketValue,
    });

    this.queryForm.get(RecipeFilterType.LANG.toLowerCase())?.enable();
  }

  private registerDomainChanges(): void {
    this.registerChanges(RecipeFilterType.DOMAIN.toLowerCase());
  }

  private registerMarketChanges(): void {
    this.registerChanges(RecipeFilterType.MARKET.toLowerCase());
  }

  private registerLangChanges(): void {
    this.registerChanges(RecipeFilterType.LANG.toLowerCase());
  }

  private registerStatusChanges(): void {
    this.registerChanges(RecipeFilterType.STATUS.toLowerCase());
    const status = { key: 'VALIDATED_PARTNER' } as Status;
    this.queryForm.get('status')?.setValue(status);
  }

  private registerRecipeReasonsChanges(): void {
    this.registerChanges(RecipeFilterType.REASONS);
  }

  private registerChanges(key: string): void {
    Utils.ensure(this.queryForm.get(key))
      .valueChanges.pipe(
        tap((value) => {
          let keyFilter = this.fieldFilters.find((fieldFilter) => fieldFilter.field === key + '.key');
          if (keyFilter) {
            this.fieldFilters.splice(this.fieldFilters.indexOf(keyFilter), 1);
          }
          if (value) {
            keyFilter = {
              field: key + '.key',
              values: [value.key],
            };
            this.fieldFilters.push(keyFilter);
          }
        }),
        switchMap((value: LocalizedKeyValue) => this.addMoreActions(key, value)),
        tap(() => this.search()),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  private addMoreActions(key: string, value: LocalizedKeyValue): Observable<void> {
    const actions: { [k: string]: (v: any) => Observable<void> } = {
      [RecipeFilterType.DOMAIN.toLowerCase()]: (v: Domain) => this.handleDomainAction(v),
      [RecipeFilterType.MARKET.toLowerCase()]: (v: Market) => this.handleMarketAction(v),
    };

    const action = actions[key];
    return action ? action(value) : of(void 0);
  }

  private handleDomainAction(domain?: Domain): Observable<void> {
    if (domain) {
      return this.refDataService.getByKey('markets', true, domain.key).pipe(
        tap((response) => {
          const selectedMarket = this.getSelected<Market>(RecipeFilterType.MARKET.toLowerCase());
          this.filteredMarkets = this.refDataService.translateReference(this.refDataService.toArray(response));
          if (!this.filteredMarkets?.some((market) => market.key === selectedMarket?.key)) {
            this.patchMarketValue(null);
            this.queryForm.get(RecipeFilterType.LANG.toLowerCase())?.disable();
          }
        }),
        map(() => void 0),
      );
    } else {
      return of(void 0).pipe(
        tap(() => {
          this.loadMarkets();
        }),
      );
    }
  }

  private handleMarketAction(market?: Market): Observable<void> {
    return of(void 0).pipe(
      tap(() => {
        if (market?.langs) {
          const marketLangKeys = (market.langs as Array<Language>).map((lang: Language) => lang.key);
          this.filteredLanguages = this.languages.filter((lang) => marketLangKeys.includes(lang.key));
          if (this.filteredLanguages.length === 1) {
            this.queryForm.patchValue({
              lang: this.filteredLanguages[0],
            });
          } else {
            this.queryForm.patchValue({
              lang: null,
            });
          }
          this.queryForm.get(RecipeFilterType.LANG.toLowerCase())?.enable();
          this.loadAndFilterMarketDomains(market);
        } else {
          this.filteredLanguages = [];
          this.queryForm.patchValue({
            lang: this.filteredLanguages[0],
          });
          this.queryForm.get(RecipeFilterType.LANG.toLowerCase())?.disable();
          this.loadDomains();
        }
      }),
    );
  }

  private getSelected<T>(formControlName: string): T {
    return Utils.ensure(this.queryForm.get(formControlName)).value;
  }

  private loadDomains(): void {
    this.filteredDomains = this.searchFiltersService.loadDomains(this.domains ?? []);
  }

  private loadMarkets(): void {
    this.searchFiltersService
      .getMarkets()
      .pipe(takeUntil(this.destroy$))
      .subscribe((markets) => {
        this.filteredMarkets = markets;
      });
  }

  private loadAndFilterMarketDomains(market: Market): void {
    this.searchFiltersService
      .getFilteredDomainsByMarket(market, this.domains)
      .pipe(takeUntil(this.destroy$))
      .subscribe((filteredDomains) => {
        this.filteredDomains = filteredDomains;
      });
  }
}
